import { useState } from "react";
import { Check } from "lucide-react";
import { Reveal, Label } from "./Motion";
import { LOOK_OPTIONS, LOOK_PRESETS } from "../data";

const STEPS = [
  { key: "cushion", no: "01", title: "Cushion Cover" },
  { key: "candle", no: "02", title: "Candle" },
  { key: "vase", no: "03", title: "Ceramic Vase" },
];

const Picker = ({ step, value, onPick }) => (
  <div>
    <p className="font-serif text-2xl text-[#2c2826] mb-4">
      <span className="text-[#b6ab9c] mr-3">{step.no}</span>{step.title}
    </p>
    <div className="grid grid-cols-3 gap-3">
      {LOOK_OPTIONS[step.key].map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            data-testid={`look-${step.key}-${o.id}`}
            onClick={() => onPick(step.key, o.id)}
            className="group text-left"
          >
            <div className={`relative aspect-square overflow-hidden border-2 transition-colors duration-300 ${active ? "border-[#985F47]" : "border-transparent"}`}>
              <img src={o.img} alt={o.label} className="h-full w-full object-cover" />
              {active && (
                <span className="absolute top-2 right-2 grid place-items-center h-6 w-6 rounded-full bg-[#985F47] text-white">
                  <Check size={14} strokeWidth={3} />
                </span>
              )}
            </div>
            <p className={`mt-2 text-xs leading-snug ${active ? "text-[#2c2826]" : "text-[#8b7e76]"}`}>{o.label}</p>
          </button>
        );
      })}
    </div>
  </div>
);

export const ShopTheLook = ({ onBundle }) => {
  const [sel, setSel] = useState({ cushion: null, candle: null, vase: null });
  const onPick = (key, id) => setSel((s) => ({ ...s, [key]: id }));

  const activePreset = LOOK_PRESETS.find(
    (p) => p.sel.cushion === sel.cushion && p.sel.candle === sel.candle && p.sel.vase === sel.vase
  );

  const chosen = STEPS.map((s) => LOOK_OPTIONS[s.key].find((o) => o.id === sel[s.key])).filter(Boolean);
  const ready = chosen.length === 3;

  const submit = () => {
    const labels = STEPS.map((s) => {
      const o = LOOK_OPTIONS[s.key].find((x) => x.id === sel[s.key]);
      return `${s.title}: ${o.label}`;
    });
    onBundle({ kind: "hamper", occasion: "Shop the Look — Curated Trio", items: labels });
  };

  return (
    <section id="shop-the-look" data-testid="shop-the-look" className="relative py-24 md:py-36 bg-[#F7F5F0]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <Reveal><Label>Shop the Look</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2c2826] leading-[1.02]">
              Bundle a curated<br /><span className="italic text-[#985F47]">trio.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[#5a5451] leading-relaxed">
              Pick one cushion, one candle and one vase — we&rsquo;ll wrap the trio into a
              ready-to-gift custom hamper and send you the details.
            </p>
          </Reveal>
        </div>

        <div className="max-w-4xl">
          <div className="space-y-10">
            <Reveal>
              <div>
                <Label>Start with a ready-made look</Label>
                <div className="mt-4 flex flex-wrap gap-3">
                  {LOOK_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`preset-${p.id}`}
                      onClick={() => setSel(p.sel)}
                      className={`rounded-full px-6 py-2.5 text-sm border transition-colors duration-300 ${
                        activePreset?.id === p.id
                          ? "bg-[#985F47] border-[#985F47] text-white"
                          : "border-[#c9bfae] text-[#5a5451] hover:border-[#985F47] hover:text-[#2c2826]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
            {STEPS.map((s) => (
              <Reveal key={s.key}><Picker step={s} value={sel[s.key]} onPick={onPick} /></Reveal>
            ))}
            <Reveal>
              <button
                data-testid="look-bundle"
                disabled={!ready}
                onClick={submit}
                className={`mt-2 w-full sm:w-auto rounded-full px-10 py-4 text-sm transition-colors duration-300 ${
                  ready ? "bg-[#2c2826] text-[#F7F5F0] hover:bg-[#985F47]" : "bg-[#e2dbd0] text-[#a99e91] cursor-not-allowed"
                }`}
              >
                {ready ? "Add to custom hamper \u2192" : "Pick all three pieces"}
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
